import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import RichTextBlockEleve from './RichTextBlockEleve';
import QCMRenderer from './renderers/QCMRenderer';
import TexteLibreRenderer from './renderers/TexteLibreRenderer';
import RelierRenderer from './renderers/RelierRenderer';
import OrdonnerRenderer from './renderers/OrdonnerRenderer';
import TexteTrouRenderer from './renderers/TexteTrouRenderer';
import ClasserRenderer from './renderers/ClasserRenderer';
import PlacerPointsRenderer from './renderers/PlacerPointsRenderer';
import ProgrammeConstructionRenderer from './renderers/ProgrammeConstructionRenderer';
import HorlogeAiguillesRenderer from './renderers/HorlogeAiguillesRenderer';
import TableResponseElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/TableResponseElementEleve';
import { getTableElementConfig } from '../../utils/questionUtils';
import Repere2DRenderer from './renderers/Repere2DRenderer';

/**
 * Modal d'aperçu élève d'une question (tous types).
 * Shell commun (header, énoncé) + routing vers le bon renderer selon type_question.
 *
 * @param {Object} question - { texte, type_question, contenu, elements_additionnels, image }
 *   - QCM :         contenu = { choix: [...], bonne_reponse: 'A' }
 *   - Texte libre :  contenu = { bonne_reponse: '42', strict: false }
 *   - Relier :       contenu = { subType: 'relier', pairs: [{source, target}, ...] }
 *   - Ordonner :     contenu = { subType: 'ordonner', items: [...] }
 *   - Texte à trous : contenu = { subType: 'texte_trou', phrase: '...', mode: 'drag', distracteurs: [...] }
 *   - Classer :      contenu = { subType: 'classer', categories: [{name, items}, ...] }
 * @param {Function} onClose - Fermer la modal
 */
const QuestionPreviewModal = ({ question, onClose }) => {
    if (!question) return null;

    const typeQuestion = question.type_question || question.type || 'qcm';
    const elements = question.elements_additionnels?.elements || [];
    const contenu = question.contenu || {};

    // Détecter un tableau : depuis elements_additionnels OU depuis bonne_reponse [ligne|colonne]
    // bonne_reponse peut être dans question directement ou dans contenu (selon la source)
    const tableConfig = getTableElementConfig({
        ...question,
        bonne_reponse: question.bonne_reponse || contenu.bonne_reponse,
    }) || null;
    // Éléments d'énoncé sans le tableau (le tableau est affiché séparément dans le bloc réponses)
    const enonceElements = tableConfig ? elements.filter(el => el.type !== 'table') : elements;

    return createPortal(
        <div
            className="fixed inset-0 z-[10010] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="w-full max-w-2xl max-h-[90vh] bg-slate-800 rounded-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 flex-shrink-0">
                    <h3 className="text-lg font-bold text-white">Aperçu élève</h3>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-white transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Contenu scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Énoncé — commun à tous les types */}
                    <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Énoncé</span>
                        {question.image ? (
                            <img src={question.image} alt="Énoncé" className="max-w-full max-h-72 rounded-lg mb-3 mx-auto object-contain" />
                        ) : (
                            <RichTextBlockEleve texte={question.texte} elements={enonceElements} />
                        )}
                    </div>

                    {/* Réponses — routing vers le bon renderer */}

                    {/* Tableau (Vrai/Faux, Multi-choix, Bijection) — détecté via elements_additionnels */}
                    {tableConfig && (
                        <div>
                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Réponses</span>
                            <TableResponseElementEleve
                                config={tableConfig}
                                reponseExistante={contenu.bonne_reponse || ''}
                                readOnly
                            />
                        </div>
                    )}

                    {(typeQuestion === 'qcm' || typeQuestion === 'qcm_multiple') && !tableConfig && (
                        <QCMRenderer
                            choix={contenu.choix}
                            bonneReponse={contenu.bonne_reponse}
                            multi={contenu.multi}
                        />
                    )}

                    {typeQuestion === 'texte' && (
                        <TexteLibreRenderer
                            bonneReponse={contenu.bonne_reponse}
                            strict={contenu.strict}
                            suffixe={contenu.suffixe}
                        />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'relier' && (
                        <RelierRenderer pairs={contenu.pairs} ciblesLeurres={contenu.cibles_leurres} />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'ordonner' && (
                        <OrdonnerRenderer items={contenu.items} />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'texte_trou' && (
                        <TexteTrouRenderer
                            phrase={contenu.phrase}
                            mode={contenu.mode}
                            distracteurs={contenu.distracteurs}
                        />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'classer' && (
                        <ClasserRenderer categories={contenu.categories} />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'placer_points' && (
                        <PlacerPointsRenderer numberline={contenu.numberline} bonneReponse={contenu.bonne_reponse} />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'placer_points_2d' && (
                        <Repere2DRenderer repere={contenu.repere} bonneReponse={contenu.bonne_reponse} dejaPlaces={contenu.dejaPlaces || []} decor={contenu.decor || null} relier={contenu.relier || null} />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'placer_aiguilles' && (
                        <HorlogeAiguillesRenderer reponse={contenu.bonne_reponse} avecTrotteuse={!!contenu.avecTrotteuse} />
                    )}

                    {typeQuestion === 'association' && contenu.subType === 'programme_construction' && (
                        <ProgrammeConstructionRenderer
                            viewBox={contenu.viewBox}
                            points={contenu.points || []}
                            preShapes={contenu.preShapes || []}
                            tasks={contenu.tasks || []}
                            pxPerCm={contenu.pxPerCm}
                            bonneReponse={contenu.bonne_reponse}
                        />
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default QuestionPreviewModal;
